import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import tokenService from '../utils/token';
import { setupActivityListener, removeActivityListener } from '../utils/sessionActivity';
import alertService from '../utils/alertService';

export const AuthContext = createContext();

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    if (token.startsWith('mock_')) return false;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const { exp } = JSON.parse(jsonPayload);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  } catch (err) {
    console.error('Failed to decode token expiration:', err);
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const logout = useCallback((reason = null) => {
    tokenService.clearTokens();
    localStorage.removeItem('ratnakar_user_data');
    setUser(null);
    setMenus([]);
    setIsAuthenticated(false);
    removeActivityListener();
    if (reason && typeof reason === 'string') {
      alertService.toast({ icon: 'warning', title: reason });
    }
    navigate('/login');
  }, [navigate]);

  const fetchUserPermissions = useCallback(async () => {
    // Configure static menus exactly matching screenshots
    setMenus([
      {
        id: 'ratnakar',
        title: 'RATNAKAR',
        icon: 'folder',
        children: [
          { id: 'callbacks', title: 'Callback Requests', path: '/view-callback' },
          { id: 'account-leads', title: 'Open Account Requests', path: '/view-open-account' },
          { id: 'add-research', title: 'Manage Research', path: '/add-research' },
          { id: 'partnerships', title: 'Partner With Us Requests', path: '/view-partner-us' },
          { id: 'enquiries', title: 'Contact Us Requests', path: '/view-contact-us' },
          { id: 'announcements', title: 'Message Notifications', path: '/message-notification' },
          { id: 'testimonials', title: 'Manage Testimonials', path: '/testimonials' },
          { id: 'website-popup', title: 'Website Popup', path: '/website-popup' }
        ]
      },
      {
        id: 'downloads',
        title: 'DOWNLOADS',
        icon: 'download',
        children: [
          { id: 'add-downloads', title: 'Manage Downloads', path: '/add-downloads' },
          { id: 'add-subsections', title: 'Download Subsections', path: '/add-subsections' }
        ]
      },
      {
        id: 'investors',
        title: 'INVESTORS',
        icon: 'business',
        children: [
          { id: 'investor-disclosures', title: 'Investor Disclosures', path: '/investor-disclosures' },
          { id: 'statutory-docs', title: 'Statutory & Registration Certificates', path: '/statutory-documents' }
        ]
      }
    ]);
  }, []);

  const login = async (username, password) => {
    setLoading(true);
    try {
      const response = await authService.login(username, password);

      // Check if API explicitly returned success: false
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Invalid username or password.');
      }

      // Real API shape: { success, message, data: { accessToken, refreshToken, admin: {...} } }
      // Fallback also handles flat shape: { accessToken, refreshToken, admin/user }
      const payload = response.data?.data ?? response.data;
      const { accessToken, refreshToken } = payload;
      const userData = payload.admin ?? payload.user ?? {};

      tokenService.setTokens(accessToken, refreshToken);
      // Also set token on apiClient default headers immediately
      // (the request interceptor reads from tokenService, so this is enough)
      localStorage.setItem('ratnakar_user_data', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);

      await fetchUserPermissions();

      setupActivityListener(() => {
        logout('Session expired due to inactivity');
      });

      const displayName = userData.username || userData.name || 'Admin';
      alertService.toast({ icon: 'success', title: `Welcome back, ${displayName}!` });
      navigate('/dashboard');
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Login failed. Please check your credentials.';
      alertService.error('Login Failed', errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (username, password) => {
    setLoading(true);
    try {
      await authService.register(username, password);
      alertService.toast({ icon: 'success', title: 'Account registered successfully! Please sign in.' });
      navigate('/login');
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Registration failed. Please try again.';
      alertService.toast({ icon: 'error', title: errorMsg });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await authService.changePassword(oldPassword, newPassword);
      alertService.toast({ icon: 'success', title: 'Password changed successfully!' });
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to change password. Please try again.';
      alertService.toast({ icon: 'error', title: errorMsg });
      throw error;
    }
  };

  const checkAuth = useCallback(async () => {
    const accessToken = tokenService.getAccessToken();
    if (!accessToken) {
      setLoading(false);
      return;
    }

    if (isTokenExpired(accessToken)) {
      console.warn('Access token expired. Attempting silent refresh...');
      try {
        const refreshRes = await authService.refreshToken();
        const refreshPayload = refreshRes.data?.data ?? refreshRes.data;
        const { accessToken: newAccess, refreshToken: newRefresh } = refreshPayload;
        tokenService.setTokens(newAccess, newRefresh);

        const storedUser = localStorage.getItem('ratnakar_user_data');
        if (storedUser) setUser(JSON.parse(storedUser));

        await fetchUserPermissions();
        setIsAuthenticated(true);

        setupActivityListener(() => {
          logout('Session expired due to inactivity');
        });
      } catch (refreshErr) {
        console.error('Refresh token invalid or expired. Directing to login.', refreshErr);
        logout();
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setIsAuthenticated(true);
      // Restore user from localStorage if profile endpoint is unavailable
      const storedUser = localStorage.getItem('ratnakar_user_data');
      if (storedUser) setUser(JSON.parse(storedUser));

      await fetchUserPermissions();

      setupActivityListener(() => {
        logout('Session expired due to inactivity');
      });
    } catch (err) {
      console.warn('Session restore failed, attempting silent refresh...', err);
      try {
        const refreshRes = await authService.refreshToken();
        // Real API refresh shape: { data: { accessToken, refreshToken } }
        const refreshPayload = refreshRes.data?.data ?? refreshRes.data;
        const { accessToken: newAccess, refreshToken: newRefresh } = refreshPayload;
        tokenService.setTokens(newAccess, newRefresh);

        await fetchUserPermissions();
        setIsAuthenticated(true);

        setupActivityListener(() => {
          logout('Session expired due to inactivity');
        });
      } catch (refreshErr) {
        console.error('Refresh token invalid. Directing to login.', refreshErr);
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout, fetchUserPermissions]);

  useEffect(() => {
    checkAuth();

    const handleLogoutEvent = (e) => {
      logout(e.detail);
    };
    window.addEventListener('auth:logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
      removeActivityListener();
    };
  }, [checkAuth, logout]);

  return (
    <AuthContext.Provider value={{
      user,
      menus,
      loading,
      isAuthenticated,
      login,
      register,
      changePassword,
      logout,
      refreshAuth: checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};
